// @file Helper functions for billing components.
import { isAppUsing } from 'bits/flip'
import { Plan } from 'types'

enum SwitchType {
  SwitchImmediately = 'switch_immediately',
  SwitchAtEndOfTerm = 'switch_at_end_of_term',
}

enum CancellationType {
  EndSubscription = 'end_subscription',
  SetAsNonRenewing = 'set_as_non_renewing',
}

function normalizedPaymentSchedule(paymentSchedule: string): 'monthly' | 'annual' {
  return paymentSchedule === 'month' ? 'monthly' : 'annual'
}

/**
 * Function that receives a plan, and returns the formatted
 * price to be displayed based on the `superscriptTierPricesVisible` experiment.
 *
 * @param {Plan} plan The plan to get the price from.
 *
 * @returns {string} The formatted price.
 */
function getFormattedPlanPriceToDisplay(plan: Plan): string {
  return isAppUsing('superscriptTierPricesVisible') ? plan.formattedToPartsPrice : plan.formattedPrice
}

/**
 * Function that formats a price, removing the decimal
 * separator and superscripting the fraction.
 *
 * @param {Intl.NumberFormatPart[]} price The price to format.
 *
 * @returns {string} The formatted price.
 */
function formatPlanPrice(price: Intl.NumberFormatPart[]): string {
  return price.reduce((formattedPrice, pricePart) => {
    // Skip the decimal separator.
    if (pricePart.type === 'decimal') return formattedPrice
    // Superscript the fraction.
    if (pricePart.type === 'fraction') return formattedPrice + `<sup>${pricePart.value}</sup>`
    return formattedPrice + pricePart.value
  }, '')
}

export { CancellationType, formatPlanPrice, getFormattedPlanPriceToDisplay, normalizedPaymentSchedule, SwitchType }
